import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import { format } from 'date-fns';
import 'react-calendar/dist/Calendar.css';

// Add custom styles for both dark and light theme calendars
const calendarStyles = `
  .simple-calendar {
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
    width: 100%;
  }
  /* Light mode */
  .light .simple-calendar {
    background-color: #fff !important;
    border-color: #e5e7eb !important;
    color: #222 !important;
  }
  .light .simple-calendar .react-calendar__navigation {
    background-color: #f3f4f6;
    color: #222;
  }
  .light .simple-calendar .react-calendar__navigation button {
    color: #6d28d9;
    background-color: transparent;
  }
  .light .simple-calendar .react-calendar__navigation button:enabled:hover,
  .light .simple-calendar .react-calendar__navigation button:enabled:focus {
    background-color: #ede9fe;
  }
  .light .simple-calendar .react-calendar__navigation__label {
    background-color: #f3f4f6 !important;
    color: #222 !important;
  }
  .light .simple-calendar .react-calendar__navigation__label:hover {
    background-color: #ede9fe !important;
  }
  .light .simple-calendar .react-calendar__month-view__weekdays {
    color: #7c3aed;
    font-weight: bold;
  }
  .light .simple-calendar .react-calendar__month-view__days__day {
    color: #222;
  }
  .light .simple-calendar .react-calendar__month-view__days__day--weekend {
    color: #be185d;
  }
  .light .simple-calendar .react-calendar__tile {
    background-color: transparent;
    color: #222;
  }
  .light .simple-calendar .react-calendar__tile:enabled:hover,
  .light .simple-calendar .react-calendar__tile:enabled:focus {
    background-color: #ede9fe;
    color: #6d28d9;
  }
  .light .simple-calendar .react-calendar__tile--active {
    background-color: #a78bfa !important;
    color: #fff;
  }
  .light .simple-calendar .react-calendar__tile--now {
    background-color: #ede9fe;
    border: 1px solid #a78bfa;
  }
  .light .simple-calendar .react-calendar__month-view__days__day--neighboringMonth {
    color: #a1a1aa;
  }
  /* Dark mode */
  .dark .simple-calendar {
    background-color: #2A2A3E !important;
    border-color: rgba(139, 92, 246, 0.2) !important;
    color: #fff !important;
  }
  .dark .simple-calendar .react-calendar__navigation {
    background-color: #1F1F2E;
    color: #fff;
  }
  .dark .simple-calendar .react-calendar__navigation button {
    color: #e4e4e7;
    background-color: transparent;
  }
  .dark .simple-calendar .react-calendar__navigation button:enabled:hover,
  .dark .simple-calendar .react-calendar__navigation button:enabled:focus {
    background-color: rgba(139, 92, 246, 0.2);
  }
  .dark .simple-calendar .react-calendar__navigation__label {
    background-color: #1F1F2E !important;
    color: #fff !important;
  }
  .dark .simple-calendar .react-calendar__navigation__label:hover {
    background-color: rgba(139, 92, 246, 0.2) !important;
  }
  .dark .simple-calendar .react-calendar__month-view__weekdays {
    color: #a78bfa;
    font-weight: bold;
  }
  .dark .simple-calendar .react-calendar__month-view__days__day {
    color: #e4e4e7;
  }
  .dark .simple-calendar .react-calendar__month-view__days__day--weekend {
    color: #fb7185;
  }
  .dark .simple-calendar .react-calendar__tile {
    background-color: transparent;
    color: #e4e4e7;
  }
  .dark .simple-calendar .react-calendar__tile:enabled:hover,
  .dark .simple-calendar .react-calendar__tile:enabled:focus {
    background-color: rgba(139, 92, 246, 0.2);
    color: #fff;
  }
  .dark .simple-calendar .react-calendar__tile--active {
    background-color: #8b5cf6 !important;
    color: #fff;
  }
  .dark .simple-calendar .react-calendar__tile--now {
    background-color: rgba(139, 92, 246, 0.1);
    border: 1px solid rgba(139, 92, 246, 0.5);
  }
  .dark .simple-calendar .react-calendar__month-view__days__day--neighboringMonth {
    color: #71717a;
  }
`;

const SimpleCalendar = ({ value, onChange, className, onClose }) => {
  const [currentView, setCurrentView] = useState(() => {
    const initialDate = value || new Date();
    return {
      activeDate: initialDate,
      selectedDate: value || null
    };
  });

  const [viewMode, setViewMode] = useState('month');

  useEffect(() => {
    if (value) {
      setCurrentView(prev => ({
        ...prev,
        activeDate: value,
        selectedDate: value
      }));
    }
  }, [value]);

  const handleDateChange = (date) => {
    setCurrentView({
      activeDate: date,
      selectedDate: date
    });
    if (onChange) {
      onChange(date);
    }
    if (onClose) {
      onClose();
    }
  };

  const handleViewChange = ({ activeStartDate, view }) => {
    setCurrentView(prev => ({
      ...prev,
      activeDate: activeStartDate
    }));
    setViewMode(view);
  };

  const customNavigationLabel = ({ date, view }) => (
    <span
      onClick={() => setViewMode(view === 'month' ? 'year' : 'month')}
      className="calendar-label hover:bg-purple-500/20 px-2 py-1 rounded text-xs font-small cursor-pointer"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setViewMode(view === 'month' ? 'year' : 'month');
        }
      }}
    >
      {format(date, 'MMM yyyy')}
    </span>
  );

  return (
    <div style={{ maxWidth: '280px', width: '100%' }}>
      {/* Add style tag to inject custom styles */}
      <style>{calendarStyles}</style>
      <Calendar
        value={currentView.selectedDate}
        onChange={handleDateChange}
        activeStartDate={currentView.activeDate}
        onActiveStartDateChange={handleViewChange}
        className={`${className || ''} simple-calendar`}
        formatShortWeekday={(_, date) => ['S', 'M', 'T', 'W', 'T', 'F', 'S'][date.getDay()]}
        showNavigation={true}
        next2Label={null}
        prev2Label={null}
        navigationLabel={customNavigationLabel}
        prevLabel="‹"
        nextLabel="›"
        view={viewMode}
        maxDetail="month"
        minDetail="year"
        onViewChange={({ view }) => setViewMode(view)}
        locale="en-GB"
      />
    </div>
  );
};

export { SimpleCalendar };
